/**
 * A focus session's plan: the lengths a student can choose, the auto rule
 * for the breaks, and the shape that follows from them. Pure numbers, no
 * state - `focusMachine` runs a session against these.
 */

export const MINUTE = 60_000;

export const FOCUS_PRESETS = [15, 25, 45] as const;
export const DEFAULT_FOCUS_MINUTES = 25;
export const MIN_FOCUS_MINUTES = 5;
export const MAX_FOCUS_MINUTES = 90;
export const FOCUS_STEP_MINUTES = 5;
export const DEFAULT_ROUNDS = 4;
export const MIN_ROUNDS = 1;
export const MAX_ROUNDS = 8;
export const SHORT_BREAK_RANGE = { min: 1, max: 30, step: 1 } as const;
export const LONG_BREAK_RANGE = { min: 5, max: 45, step: 5 } as const;

/** A pause this long closes the block, crediting what was done. */
export const PAUSE_LIMIT_MS = 30 * MINUTE;
/** A block shorter than this was a mis-tap, not focus: nothing is credited. */
export const MIN_CREDIT_MS = MINUTE;
/** A block that ended more than this long ago ended "while you were away". */
export const AWAY_THRESHOLD_MS = MINUTE;

export const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

export function clampFocusMinutes(minutes: number): number {
  return clamp(Math.round(minutes), MIN_FOCUS_MINUTES, MAX_FOCUS_MINUTES);
}

/** A short break of a fifth of the focus time: 25 → 5, clamped 3-15. */
export const autoShortBreak = (focusMinutes: number) =>
  clamp(Math.round(focusMinutes / 5), 3, 15);
/** A long break of three short ones: 5 → 15, clamped 10-30. */
export const autoLongBreak = (shortMinutes: number) =>
  clamp(shortMinutes * 3, 10, 30);
/**
 * The long break goes halfway through a session of 4 rounds or more (after 2
 * of 4, 3 of 6, 4 of 8), where focus dips. The end of the session is its own
 * rest, so a shorter session has none.
 */
export const longBreakAfter = (rounds: number): number | null =>
  rounds >= 4 ? Math.floor(rounds / 2) : null;

export interface SessionPlanInput {
  focusMinutes: number;
  rounds: number;
  /** `null` or absent: follow the focus length (auto). */
  shortMinutes?: number | null;
  longMinutes?: number | null;
}

export interface SessionPlan {
  focusMinutes: number;
  rounds: number;
  shortMinutes: number;
  longMinutes: number;
  longAfter: number | null;
  /** Focus, plus every break between the rounds; none after the last. */
  totalMs: number;
}

/** A timed session's shape: the rounds, the breaks between them, the total. */
export function sessionPlan(input: SessionPlanInput): SessionPlan {
  const focusMinutes = clampFocusMinutes(input.focusMinutes);
  const rounds = clamp(Math.round(input.rounds), MIN_ROUNDS, MAX_ROUNDS);
  const shortMinutes = clamp(
    input.shortMinutes ?? autoShortBreak(focusMinutes),
    SHORT_BREAK_RANGE.min,
    SHORT_BREAK_RANGE.max
  );
  const longMinutes = clamp(
    input.longMinutes ?? autoLongBreak(shortMinutes),
    LONG_BREAK_RANGE.min,
    LONG_BREAK_RANGE.max
  );
  const longAfter = longBreakAfter(rounds);
  const breaks =
    rounds > 1
      ? (rounds - 1 - (longAfter ? 1 : 0)) * shortMinutes +
        (longAfter ? longMinutes : 0)
      : 0;
  return {
    focusMinutes,
    rounds,
    shortMinutes,
    longMinutes,
    longAfter,
    totalMs: (rounds * focusMinutes + breaks) * MINUTE,
  };
}

/** Flow's break: a fifth of the focus time, clamped to 3-20 minutes. */
export function flowBreakMs(focusMs: number): number {
  const minutes = Math.round(focusMs / 5 / MINUTE);
  return clamp(minutes, 3, 20) * MINUTE;
}
